import React from 'react';
import { useParams } from 'react-router-dom';

import { Button, Heading, Text } from '@auspost/postmaster-react';

import NicknameInput from 'App/DetailsPage/common/NicknameInput/NicknameInput';
import { ReactComponent as QuickEditIcon } from 'assets/quick-edit.svg';
import { CommonClass } from 'config/track-config';
import { selectIsAuthed } from 'store/customer/customer.selectors';
import { saveNickname } from 'store/shipment/thunks/saveNickname.thunk';
import { useAppDispatch, useAppSelector } from 'store/store';

import { useArticles, useCurrentArticle, useShipment } from '../../hooks/shipment.hooks';
import ConsignmentIcon from './ConsignmentIcon/ConsignmentIcon';
import {
  ANON_TRACKING_NUMBER_TEST_ID,
  NICKNAME_CANCEL_TEST_ID,
  NICKNAME_CONSIGNMENT_ICON_TEST_ID,
  NICKNAME_FORM_TEST_ID,
  NICKNAME_SAVE_TEST_ID,
  NICKNAME_TEST_ID,
} from './Nickname.test-ids';

import styles from './Nickname.module.scss';

const Nickname: React.FC = () => {
  const dispatch = useAppDispatch();
  const articles = useArticles();
  const { articleId: trackingNumber, nickname: existingNickname } = useCurrentArticle() ?? {};
  const consignmentId = useShipment()?.consignmentId || '';
  const firstRender = React.useRef(true);
  const isAuthed = useAppSelector(selectIsAuthed);
  const { loading, error } = useAppSelector((state) => state.shipmentDetails.saveNickname) ?? {};
  const isConsignment = articles.length > 1;

  const [editMode, setEditMode] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(existingNickname ?? '');
  const nicknameButtonRef = React.createRef<HTMLButtonElement>();

  const { trackingId } = useParams();

  // revert nickname to existing name when error
  React.useEffect(() => {
    if (error) setInputValue(existingNickname ?? '');
  }, [error, existingNickname]);

  // skip focusing on first render and set focus on nickname
  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (nicknameButtonRef.current) {
      nicknameButtonRef.current.focus();
    }
  }, [editMode]);

  // set existing nickname in input
  React.useEffect(() => {
    setInputValue(existingNickname ?? '');
  }, [existingNickname]);

  const handleCancelEdit = () => {
    setInputValue(existingNickname ?? '');
    setEditMode(false);
  };

  if (!trackingId) return null;

  const updateNickname = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await dispatch(saveNickname({ trackingId, nickname: inputValue }));
    setEditMode(false);
  };

  const displayEditMode = () => {
    return (
      <form className={styles.nicknameForm} data-testid={NICKNAME_FORM_TEST_ID} onSubmit={(e) => updateNickname(e)}>
        <div>
          <NicknameInput
            nicknameId='nicknameInput'
            inputValue={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && handleCancelEdit()}
            ariaLabel='Edit Nickname'
          />
          <Text variant='body-small' className={styles.characterLimit}>
            30 Character limit
          </Text>
        </div>
        <div className={styles.nicknameFormButtons}>
          <Button variant='primary' size='small' type='submit' isLoading={loading} data-testid={NICKNAME_SAVE_TEST_ID}>
            Save
          </Button>
          <Button
            variant='tertiary-filled'
            size='small'
            type='button'
            onClick={handleCancelEdit}
            data-testid={NICKNAME_CANCEL_TEST_ID}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    );
  };

  const showConsignment = () => {
    if (!isConsignment || editMode) return null;
    return (
      <ConsignmentIcon
        size={articles.length}
        className={styles.consignmentIcon}
        data-testid={NICKNAME_CONSIGNMENT_ICON_TEST_ID}
      />
    );
  };

  const displayAnonTrackingNumber = () => {
    return (
      <Heading level='h3'>
        <Text variant='subtitle' className={styles.anonField}>
          TRACKING NUMBER
        </Text>
        <div
          className={styles.anonTrackingNumber}
          aria-label={`Tracking number`}
          data-testid={ANON_TRACKING_NUMBER_TEST_ID}
        >
          <span>{isConsignment ? consignmentId : trackingNumber}</span>
          {showConsignment()}
        </div>
      </Heading>
    );
  };

  const displayNickname = () => {
    if (!isAuthed) return displayAnonTrackingNumber();
    if (editMode) return displayEditMode();
    return (
      <Heading level='h1'>
        <button
          data-testid={NICKNAME_TEST_ID}
          className={existingNickname ? CommonClass.FULLSTORY_MASK : styles.default}
          onClick={() => isAuthed && setEditMode(true)}
          ref={nicknameButtonRef}
          aria-label='Press to change nickname.'
        >
          {existingNickname || 'Name this parcel'}
          <QuickEditIcon />
          {showConsignment()}
        </button>
      </Heading>
    );
  };

  return displayNickname();
};

export default Nickname;
